import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  setDoc,
  deleteDoc,
} from 'firebase/firestore';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';

const emptyForm = {
  registrationMode: 'team',
  initiationEnabled: false,
  initiationPrice: 0,
  initiationCapacity: 16,
  id: '',
  name: '',
  players: 16,
  location: '',
  date: '',
  time: 'de 14h à 18h30',

  // Image/logo affichée dans TournamentList
  picture: '',

  // Grande image affichée dans TournamentRegistration
  registrationPicture: '',

  price: 60,
  invoiceVatRate: 21,
  mealOptionEnabled: false,
  mealPrice: '',
  mealDescription: '',
  eloMin: 0,
  eloMax: 2700,
  region: '',
  badge: '',
  badgef: '',
  visible: true,
  archived: false,
};

const regions = [
  'Namur',
  'Hainaut',
  'Brabant Wallon',
  'Liège',
  'Luxembourg',
];

const AdminTeamBuildingCatalog = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [tournaments, setTournaments] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedIndividualEvent, setSelectedIndividualEvent] = useState(null);
  const [individualRegistrations, setIndividualRegistrations] = useState([]);
  const [loadingIndividualRegistrations, setLoadingIndividualRegistrations] = useState(false);

  const [registrationImageFile, setRegistrationImageFile] = useState(null);
  const [uploadingRegistrationImage, setUploadingRegistrationImage] = useState(false);

  const isBusy = loading || uploadingRegistrationImage;

  const loadTournaments = async () => {
    try {
      setLoadingList(true);

      const snapshot = await getDocs(collection(db, 'team_building_catalog'));

      const data = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const catalogData = docSnap.data();
          const id = Number(catalogData.id ?? docSnap.id);

          let registrationPicture = '';
          let mealOptionEnabled = false;
          let mealPrice = 0;
          let mealDescription = '';

          try {
            const registrationSnap = await getDoc(
              doc(db, 'tournament_registrations', String(id))
            );

            if (registrationSnap.exists()) {
              const registrationData = registrationSnap.data();

              registrationPicture = registrationData.registrationPicture || '';
              mealOptionEnabled = Boolean(registrationData.mealOptionEnabled);
              mealPrice = registrationData.mealPrice || 0;
              mealDescription = registrationData.mealDescription || '';
            }
          } catch (error) {
            console.warn(
              'Impossible de récupérer registrationPicture pour le tournoi',
              id,
              error
            );
          }

          return {
            ...catalogData,
            registrationMode: catalogData.registrationMode === 'individual' ? 'individual' : 'team',
            _docId: docSnap.id,
            id,
            registrationPicture: catalogData.registrationPicture || registrationPicture,
            mealOptionEnabled,
            mealPrice,
            mealDescription,
          };
        })
      );

      data.sort((a, b) => Number(a.id || 0) - Number(b.id || 0));

      setTournaments(data);
    } catch (error) {
      console.error('Erreur chargement team_building_catalog:', error);
      alert('Erreur lors du chargement des tournois.');
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadTournaments();
  }, []);

  const nextId = useMemo(() => {
    if (!tournaments.length) return 10001;

    return Math.max(...tournaments.map((t) => Number(t.id || 0)), 10000) + 1;
  }, [tournaments]);

  const filteredTournaments = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return tournaments;

    return tournaments.filter((tournament) => (
      String(tournament.id || '').includes(term) ||
      String(tournament.name || '').toLowerCase().includes(term) ||
      String(tournament.location || '').toLowerCase().includes(term) ||
      String(tournament.region || '').toLowerCase().includes(term) ||
      String(tournament.date || '').toLowerCase().includes(term)
    ));
  }, [search, tournaments]);

  const getTournamentDocId = (tournament) => {
    return String(tournament._docId || tournament.id);
  };

  const scrollToForm = () => {
    setTimeout(() => {
      document
        .getElementById('admin-tournament-form')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const resetForm = () => {
    setForm(emptyForm);
    setRegistrationImageFile(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const setNextTournament = () => {
    setForm((prev) => ({
      ...prev,
      id: nextId,
      name: `Team Building n°${nextId}`,
    }));

    setRegistrationImageFile(null);
    scrollToForm();
  };

  const uploadRegistrationPicture = async () => {
    if (!registrationImageFile) {
      return form.registrationPicture.trim();
    }

    if (!form.id) {
      alert("Mets d'abord un ID de tournoi avant d'uploader une image.");
      return null;
    }

    try {
      setUploadingRegistrationImage(true);

      const storage = getStorage(
        undefined,
        'gs://mendyx-adafe.firebasestorage.app'
      );

      const extension = registrationImageFile.name.split('.').pop() || 'jpg';

      const cleanName = registrationImageFile.name
        .replace(/\.[^/.]+$/, '')
        .replace(/[^a-zA-Z0-9-_]/g, '-')
        .toLowerCase();

      const fileName = `team-building-registration-${form.id}-${Date.now()}-${cleanName}.${extension}`;

      const storageRef = ref(
        storage,
        `team-building/${form.id}/registration/${fileName}`
      );

      await uploadBytes(storageRef, registrationImageFile, {
        contentType: registrationImageFile.type || 'image/jpeg',
      });

      const downloadURL = await getDownloadURL(storageRef);

      setForm((prev) => ({
        ...prev,
        registrationPicture: downloadURL,
      }));

      setRegistrationImageFile(null);

      return downloadURL;
    } catch (error) {
      console.error('Erreur upload image inscription tournoi:', error);
      alert("Erreur lors de l'upload de l'image d'inscription.");
      return null;
    } finally {
      setUploadingRegistrationImage(false);
    }
  };

  const buildTournamentPayload = () => {
    const payload = {
      registrationMode: form.registrationMode === 'individual' ? 'individual' : 'team',
      initiationEnabled: form.registrationMode === 'team' && Boolean(form.initiationEnabled),
      initiationPrice: Number(form.initiationPrice),
      initiationCapacity: Number(form.initiationCapacity),
      id: Number(form.id),
      name: form.name.trim(),
      players: Number(form.players),
      location: form.location.trim(),
      date: form.date,
      time: form.time.trim(),

      // Image/logo de la card dans TournamentList
      picture: form.picture.trim(),

      price: Number(form.price),
      invoiceVatRate: Number(form.invoiceVatRate ?? 21),
      mealOptionEnabled: Boolean(form.mealOptionEnabled),
      mealPrice: Number(form.mealPrice || 0),
      mealDescription: form.mealDescription.trim(),
      eloMin: Number(form.eloMin),
      eloMax: Number(form.eloMax),
      region: form.region.trim(),
      visible: Boolean(form.visible),
      archived: Boolean(form.archived),
      tournamentType: 'team_building',
    };

    if (form.badge.trim()) {
      payload.badge = form.badge.trim();
    }

    if (form.badgef.trim()) {
      payload.badgef = form.badgef.trim();
    }

    return payload;
  };

  const buildTournamentRegistrationPayload = (payload, registrationPictureUrl) => {
    return {
      price: Number(payload.price),
      priceBasis: 'person',
      invoiceVatRate: Number(payload.invoiceVatRate ?? 21),
      mealOptionEnabled: Boolean(payload.mealOptionEnabled),
      mealPrice: Number(payload.mealPrice || 0),
      mealDescription: payload.mealDescription || '',
      players: Number(payload.players),
      eloMin: Number(payload.eloMin),
      eloMax: Number(payload.eloMax),

      // Image du formulaire dans TournamentRegistration
      registrationPicture: registrationPictureUrl || '',
      tournamentType: 'team_building',
    };
  };

  const validateForm = () => {
    if (form.registrationMode === 'team' && form.initiationEnabled) {
      if (!Number.isFinite(Number(form.initiationPrice)) || Number(form.initiationPrice) < 0) return 'Prix initiation invalide.';
      if (!Number.isInteger(Number(form.initiationCapacity)) || Number(form.initiationCapacity) < 1) return 'La capacité initiation doit être un entier positif.';
    }
    if (!form.id) return 'ID obligatoire.';
    if (!form.name.trim()) return 'Nom du tournoi obligatoire.';
    if (!form.players) return "Nombre d'équipes obligatoire.";
    if (!form.location.trim()) return 'Lieu obligatoire.';
    if (!form.date) return 'Date obligatoire.';
    if (!form.time.trim()) return 'Heure obligatoire.';

    if (!form.picture.trim()) {
      return 'Image/logo de la card obligatoire. Exemple : tero.jfif.';
    }

    if (Number(form.price) < 0) return 'Le prix ne peut pas être négatif.';

    if (form.registrationMode === 'team' && form.mealOptionEnabled && Number(form.mealPrice || 0) <= 0) {
      return "Prix du repas obligatoire si l'option repas est activÃ©e.";
    }

    if (Number(form.eloMax) < Number(form.eloMin)) {
      return 'ELO max ne peut pas être inférieur à ELO min.';
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errorMessage = validateForm();

    if (errorMessage) {
      alert(errorMessage);
      return;
    }

    try {
      setLoading(true);

      const registrationPictureUrl = await uploadRegistrationPicture();

      if (registrationPictureUrl === null) {
        return;
      }

      const payload = buildTournamentPayload();
      payload.registrationPicture = registrationPictureUrl || '';
      const registrationPayload = buildTournamentRegistrationPayload(
        payload,
        registrationPictureUrl
      );

      const documentId = String(payload.id);

      await setDoc(
        doc(db, 'team_building_catalog', documentId),
        payload,
        { merge: true }
      );

      if (payload.registrationMode === 'team') {
        await setDoc(
          doc(db, 'tournament_registrations', documentId),
          registrationPayload,
          { merge: true }
        );
      }

      alert(
        `✅ Team Building ${payload.id} enregistré dans team_building_catalog et tournament_registrations.`
      );

      setForm(emptyForm);
      setRegistrationImageFile(null);
      await loadTournaments();
    } catch (error) {
      console.error('Erreur enregistrement Team Building:', error);
      alert("Erreur lors de l'enregistrement du Team Building.");
    } finally {
      setLoading(false);
    }
  };

  const editTournament = (tournament) => {
    setForm({
      initiationEnabled: Boolean(tournament.initiationEnabled),
      initiationPrice: tournament.initiationPrice ?? 0,
      initiationCapacity: tournament.initiationCapacity ?? 16,
      registrationMode: tournament.registrationMode === 'individual' ? 'individual' : 'team',
      id: tournament.id ?? '',
      name: tournament.name ?? '',
      players: tournament.players ?? 16,
      location: tournament.location ?? '',
      date: tournament.date ?? '',
      time: tournament.time ?? 'de 14h à 18h30',
      picture: tournament.picture ?? '',
      registrationPicture: tournament.registrationPicture ?? '',
      price: tournament.price ?? 60,
      invoiceVatRate: tournament.invoiceVatRate ?? 21,
      mealOptionEnabled: Boolean(tournament.mealOptionEnabled),
      mealPrice: tournament.mealPrice ?? '',
      mealDescription: tournament.mealDescription ?? '',
      eloMin: tournament.eloMin ?? 0,
      eloMax: tournament.eloMax ?? 2700,
      region: tournament.region ?? '',
      badge: tournament.badge ?? '',
      badgef: tournament.badgef ?? '',
      visible: tournament.visible ?? true,
      archived: tournament.archived ?? false,
    });

    setRegistrationImageFile(null);
    scrollToForm();
  };

  const duplicateTournament = (tournament) => {
    setForm({
      initiationEnabled: Boolean(tournament.initiationEnabled),
      initiationPrice: tournament.initiationPrice ?? 0,
      initiationCapacity: tournament.initiationCapacity ?? 16,
      registrationMode: tournament.registrationMode === 'individual' ? 'individual' : 'team',
      id: nextId,
      name: `Team Building n°${nextId}`,
      players: tournament.players ?? 16,
      location: tournament.location ?? '',
      date: tournament.date ?? '',
      time: tournament.time ?? 'de 14h à 18h30',
      picture: tournament.picture ?? '',
      registrationPicture: tournament.registrationPicture ?? '',
      price: tournament.price ?? 60,
      invoiceVatRate: tournament.invoiceVatRate ?? 21,
      mealOptionEnabled: Boolean(tournament.mealOptionEnabled),
      mealPrice: tournament.mealPrice ?? '',
      mealDescription: tournament.mealDescription ?? '',
      eloMin: tournament.eloMin ?? 0,
      eloMax: tournament.eloMax ?? 2700,
      region: tournament.region ?? '',
      badge: tournament.badge ?? '',
      badgef: tournament.badgef ?? '',
      visible: true,
      archived: false,
    });

    setRegistrationImageFile(null);
    scrollToForm();
  };

  const toggleVisibility = async (tournament) => {
    try {
      setLoading(true);

      const documentId = getTournamentDocId(tournament);

      await setDoc(
        doc(db, 'team_building_catalog', documentId),
        {
          visible: !Boolean(tournament.visible),
        },
        { merge: true }
      );

      await loadTournaments();
    } catch (error) {
      console.error('Erreur visibilité tournoi:', error);
      alert('Erreur lors du changement de visibilité.');
    } finally {
      setLoading(false);
    }
  };

  const archiveTournament = async (tournament) => {
    const confirmArchive = window.confirm(
      `Archiver le Team Building ${tournament.id} ? Il ne sera plus visible sur le site.`
    );

    if (!confirmArchive) return;

    try {
      setLoading(true);

      const documentId = getTournamentDocId(tournament);

      await setDoc(
        doc(db, 'team_building_catalog', documentId),
        {
          archived: true,
          visible: false,
        },
        { merge: true }
      );

      await loadTournaments();
    } catch (error) {
      console.error('Erreur archivage Team Building:', error);
      alert("Erreur lors de l'archivage.");
    } finally {
      setLoading(false);
    }
  };

  const deleteTournament = async (tournament) => {
    const confirmDelete = window.confirm(
      `Supprimer définitivement le Team Building ${tournament.id} de team_building_catalog ? Les inscriptions et tournament_registrations ne seront pas supprimés.`
    );

    if (!confirmDelete) return;

    try {
      setLoading(true);

      const documentId = getTournamentDocId(tournament);

      await deleteDoc(doc(db, 'team_building_catalog', documentId));

      await loadTournaments();
    } catch (error) {
      console.error('Erreur suppression Team Building:', error);
      alert('Erreur lors de la suppression du tournoi.');
    } finally {
      setLoading(false);
    }
  };

  const openIndividualRegistrations = async (event) => {
    try {
      setSelectedIndividualEvent(event);
      setIndividualRegistrations([]);
      setLoadingIndividualRegistrations(true);
      const snapshot = await getDocs(
        collection(db, 'team_building_registrations', String(event.id), 'inscriptions')
      );
      setIndividualRegistrations(snapshot.docs.map((document) => ({ id: document.id, ...document.data() })));
    } catch (error) {
      console.error('Erreur chargement inscriptions individuelles :', error);
      alert('Impossible de charger les inscriptions individuelles.');
    } finally {
      setLoadingIndividualRegistrations(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-[#4c2f8e]">
          Admin - Team Building
        </h1>

        <p className="text-gray-600 mt-2">
          Ajoute, modifie, duplique ou archive les événements privés dans team_building_catalog.
        </p>

        <div className="mt-3 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-800">
          Ce panel crée ou met à jour aussi tournament_registrations avec price,
          players, eloMin, eloMax et registrationPicture. Les inscriptions existantes ne sont pas supprimées.
        </div>
      </div>

      <form
        id="admin-tournament-form"
        onSubmit={handleSubmit}
        className="bg-white shadow-lg rounded-2xl p-4 md:p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        <div className="md:col-span-2 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <h2 className="text-xl font-bold text-gray-800">
            Ajouter / Modifier un Team Building
          </h2>

          <div className="flex flex-col md:flex-row gap-2">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setNextTournament();
              }}
              className="px-4 py-2 rounded-full bg-gray-900 text-white text-sm"
            >
              Pré-remplir prochain ID : {nextId}
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                resetForm();
              }}
              className="px-4 py-2 rounded-full bg-gray-200 text-gray-800 text-sm"
            >
              Vider le formulaire
            </button>
          </div>
        </div>

        <div className="md:col-span-2 rounded-xl border border-purple-200 bg-purple-50 p-4">
          <label className="block text-sm font-semibold mb-1">Type d'inscription</label>
          <select
            name="registrationMode"
            value={form.registrationMode}
            onChange={handleChange}
            className="w-full border rounded-lg p-2 bg-white"
          >
            <option value="team">Tournoi (2 personnes), avec initiation facultative</option>
            <option value="individual">Individuelle / initiation seule (1 personne)</option>
          </select>
          <p className="mt-2 text-xs text-gray-600">
            Choisissez un tournoi, auquel vous pouvez ajouter un cours d’initiation, ou une inscription individuelle seule.
          </p>
        </div>

        {form.registrationMode === 'team' && (
          <section className="md:col-span-2 rounded-xl border border-purple-200 bg-purple-50 p-4">
            <label className="flex items-center gap-3 font-semibold">
              <input type="checkbox" name="initiationEnabled" checked={form.initiationEnabled} onChange={handleChange} />
              Activer l’option initiation (cours)
            </label>
            <p className="mt-2 text-sm text-gray-600">Le participant choisit : initiation pour 1 personne ou tournoi pour 2 personnes.</p>
            {form.initiationEnabled && <div className="mt-4 grid gap-4 md:grid-cols-2">
              <label>Prix initiation par personne (€)
                <input className="mt-1 w-full rounded-lg border p-2" type="number" name="initiationPrice" min="0" step="0.01" required value={form.initiationPrice} onChange={handleChange} />
              </label>
              <label>Nombre maximum de personnes à l’initiation
                <input className="mt-1 w-full rounded-lg border p-2" type="number" name="initiationCapacity" min="1" step="1" required value={form.initiationCapacity} onChange={handleChange} />
              </label>
            </div>}
          </section>
        )}

        <div>
          <label className="block text-sm font-semibold mb-1">ID</label>
          <input
            name="id"
            type="number"
            value={form.id}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="10001"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Nom</label>
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="Team Building n°10001"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">
            {form.registrationMode === 'individual' ? 'Nombre maximum de personnes' : "Nombre maximum d’équipes (2 personnes par équipe)"}
          </label>
          <input
            name="players"
            type="number"
            value={form.players}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="16"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">
            Prix par personne (€)
          </label>
          <input
            name="price"
            type="number"
            min="0"
            step="0.01"
            value={form.price}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="60"
            required
          />
        </div>

        {(
          <div>
            <label className="block text-sm font-semibold mb-1">Taux de TVA pour la facture (%)</label>
            <input
              name="invoiceVatRate"
              type="number"
              min="0"
              step="0.01"
              value={form.invoiceVatRate}
              onChange={handleChange}
              className="w-full border rounded-lg p-2"
            />
          </div>
        )}

        <div className={`md:col-span-2 rounded-xl border p-4 bg-gray-50 ${form.registrationMode === 'individual' ? 'hidden' : ''}`}>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="mealOptionEnabled"
              name="mealOptionEnabled"
              checked={form.mealOptionEnabled}
              onChange={handleChange}
              className="h-4 w-4"
            />
            <label htmlFor="mealOptionEnabled" className="text-sm font-semibold">
              Proposer une option repas sur ce Team Building
            </label>
          </div>

          {form.mealOptionEnabled && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="mealPrice" className="block text-sm font-semibold mb-1">
                  Prix du repas
                </label>
                <input
                  id="mealPrice"
                  name="mealPrice"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.mealPrice}
                  onChange={handleChange}
                  placeholder="Ex: 12"
                  className="w-full border rounded-lg p-2"
                />
              </div>

              <div>
                <label htmlFor="mealDescription" className="block text-sm font-semibold mb-1">
                  Description du repas
                </label>
                <input
                  id="mealDescription"
                  name="mealDescription"
                  value={form.mealDescription}
                  onChange={handleChange}
                  placeholder="Ex: Burger + boisson"
                  className="w-full border rounded-lg p-2"
                />
              </div>
            </div>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold mb-1">Lieu complet</label>
          <input
            name="location"
            value={form.location}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="Tero Padel Charleroi | Rue de la Pépinière 5, 6041 Charleroi"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Date</label>
          <input
            name="date"
            type="date"
            value={form.date}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Heure</label>
          <input
            name="time"
            value={form.time}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="de 14h à 18h30"
            required
          />
        </div>

        <div className="md:col-span-2 border rounded-xl p-4 bg-gray-50">
          <label className="block text-sm font-semibold mb-1">
            Image/logo de la carte Team Building
          </label>

          <input
            name="picture"
            value={form.picture}
            onChange={handleChange}
            className="w-full border rounded-lg p-2 bg-white"
            placeholder="tero.jfif"
            required
          />

          <p className="text-xs text-gray-500 mt-1">
            Sert uniquement à la card dans TournamentList. Exemple : tero.jfif, h5.jfif, padelone.jfif, smash.jfif.
          </p>

          {form.picture && (
            <div className="mt-3">
              <p className="text-xs text-gray-500 mb-1">Aperçu image/logo card :</p>

              <img
                src={form.picture}
                alt="Aperçu card tournoi"
                className="w-32 h-32 object-contain rounded-lg border bg-white"
              />
            </div>
          )}
        </div>

        <div className="md:col-span-2 border rounded-xl p-4 bg-purple-50">
          <label className="block text-sm font-semibold mb-1">
            Image du formulaire d’inscription Team Building
          </label>

          <input
            type="file"
            accept="image/*"
            onChange={(e) => setRegistrationImageFile(e.target.files?.[0] || null)}
            className="w-full border rounded-lg p-2 bg-white"
          />

          <p className="text-xs text-gray-500 mt-1">
            Cette image sera affichée à gauche du formulaire d’inscription du Team Building.
          </p>

          {registrationImageFile && (
            <div className="mt-2 text-sm text-gray-700">
              Nouvelle image sélectionnée : <strong>{registrationImageFile.name}</strong>
            </div>
          )}

          {form.registrationPicture && (
            <div className="mt-3">
              <p className="text-xs text-gray-500 mb-1">
                Image actuelle du formulaire :
              </p>

              <img
                src={form.registrationPicture}
                alt="Aperçu inscription Team Building"
                className="w-48 h-48 object-contain rounded-lg border bg-white"
              />

              <label className="block text-xs font-semibold text-gray-500 mt-3 mb-1">
                URL enregistrée
              </label>

              <input
                name="registrationPicture"
                value={form.registrationPicture}
                onChange={handleChange}
                className="w-full border rounded-lg p-2 text-xs bg-white"
                placeholder="URL Firebase Storage"
              />
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Région</label>
          <select
            name="region"
            value={form.region}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
          >
            <option value="">-- Choisir une région --</option>
            {regions.map((region) => (
              <option key={region} value={region}>
                {region}
              </option>
            ))}
          </select>
        </div>

        <div className={form.registrationMode === 'individual' ? 'hidden' : ''}>
          <label className="block text-sm font-semibold mb-1">ELO min</label>
          <input
            name="eloMin"
            type="number"
            value={form.eloMin}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="0"
          />
        </div>

        <div className={form.registrationMode === 'individual' ? 'hidden' : ''}>
          <label className="block text-sm font-semibold mb-1">ELO max</label>
          <input
            name="eloMax"
            type="number"
            value={form.eloMax}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="2700"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Badge</label>
          <input
            name="badge"
            value={form.badge}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="MIXTE / PLUS GRAND TOURNOI..."
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Badge féminin</label>
          <input
            name="badgef"
            value={form.badgef}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="100% Féminin"
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            name="visible"
            type="checkbox"
            checked={form.visible}
            onChange={handleChange}
          />
          <label>Visible sur le site</label>
        </div>

        <div className="flex items-center gap-2">
          <input
            name="archived"
            type="checkbox"
            checked={form.archived}
            onChange={handleChange}
          />
          <label>Archivé</label>
        </div>

        <button
          type="submit"
          disabled={isBusy}
          className="md:col-span-2 bg-gradient-to-r from-[#4c2f8e] to-[#ea4c89] text-white rounded-full py-3 font-semibold shadow-lg disabled:opacity-60"
        >
          {isBusy ? 'Enregistrement...' : 'Enregistrer le Team Building'}
        </button>
      </form>

      <div className="bg-white shadow-lg rounded-2xl p-4 md:p-6">
        <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-800">
            Team Building dans team_building_catalog
          </h2>

          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border rounded-lg p-2 w-full md:w-80"
            placeholder="Recherche ID, club, région, date..."
          />
        </div>

        {loadingList ? (
          <p className="text-gray-500">Chargement...</p>
        ) : filteredTournaments.length === 0 ? (
          <p className="text-gray-500">
            Aucun Team Building trouvé dans team_building_catalog.
          </p>
        ) : (
          <div className="space-y-3">
            {filteredTournaments.map((tournament) => (
              <div
                key={getTournamentDocId(tournament)}
                className={`border rounded-xl p-4 ${
                  tournament.archived ? 'bg-gray-100 opacity-70' : 'bg-white'
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div>
                    <div className="font-bold text-lg text-[#4c2f8e]">
                      #{tournament.id} - {tournament.name}
                    </div>
                    <div className="mt-1 inline-block rounded-full bg-purple-100 px-3 py-1 text-xs font-bold text-purple-700">
                      {tournament.registrationMode === 'individual' ? 'Inscription individuelle' : 'Inscription par équipe'}
                    </div>

                    <div className="text-sm text-gray-600 mt-1">
                      {tournament.date} | {tournament.time} | {tournament.region || 'Sans région'}
                    </div>

                    <div className="text-sm text-gray-600">
                      {tournament.location}
                    </div>

                    <div className="text-sm mt-1">
                      {tournament.registrationMode === 'individual' ? `${tournament.players} personnes` : `${tournament.players * 2} personnes (${tournament.players} équipes)`} | {tournament.price}€ / personne
                      {tournament.registrationMode !== 'individual' && ` — ${Number(tournament.price) * 2}€ / équipe`}
                      {tournament.registrationMode !== 'individual' && ` | ELO ${tournament.eloMin} - ${tournament.eloMax}`}
                    </div>

                    <div className="text-xs text-gray-500 mt-1">
                      Repas : {tournament.mealOptionEnabled ? `oui (${tournament.mealPrice || 0}â‚¬)` : 'non'}
                    </div>

                    {tournament.mealOptionEnabled && tournament.mealDescription && (
                      <div className="text-xs text-gray-500 mt-1">
                        Description repas : {tournament.mealDescription}
                      </div>
                    )}

                    <div className="text-xs text-gray-500 mt-1 break-all">
                      Image card : {tournament.picture || 'Aucune'}
                    </div>

                    <div className="text-xs text-gray-500 mt-1 break-all">
                      Image formulaire : {tournament.registrationPicture || 'Aucune'}
                    </div>

                    <div className="text-xs text-gray-500 mt-1">
                      Visible : {tournament.visible ? 'oui' : 'non'} | Archivé : {tournament.archived ? 'oui' : 'non'}
                    </div>

                    {tournament.badge && (
                      <div className="inline-block mt-2 mr-2 px-3 py-1 text-xs rounded-full bg-purple-100 text-purple-700">
                        {tournament.badge}
                      </div>
                    )}

                    {tournament.badgef && (
                      <div className="inline-block mt-2 px-3 py-1 text-xs rounded-full bg-pink-100 text-pink-700">
                        {tournament.badgef}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {tournament.registrationMode !== 'individual' && <button
                      type="button"
                      disabled={isBusy}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate(`/tournament/${encodeURIComponent(tournament.id)}/viewadmin`);
                      }}
                      className="px-3 py-2 rounded-lg bg-[#4c2f8e] text-white text-sm disabled:opacity-60"
                    >
                      GÃ©rer poules/scores
                    </button>}

                    {tournament.registrationMode !== 'individual' && <button
                      type="button"
                      disabled={isBusy}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate(`/tournament/${encodeURIComponent(tournament.id)}/view`);
                      }}
                      className="px-3 py-2 rounded-lg bg-purple-100 text-[#4c2f8e] text-sm disabled:opacity-60"
                    >
                      Visualiser le tournoi
                    </button>}

                    {(tournament.registrationMode === 'individual' || tournament.initiationEnabled) && (
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => openIndividualRegistrations(tournament)}
                        className="px-3 py-2 rounded-lg bg-[#4c2f8e] text-white text-sm disabled:opacity-60"
                      >
                        {tournament.initiationEnabled ? 'Participants initiation' : 'Voir les participants'}
                      </button>
                    )}

                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        editTournament(tournament);
                      }}
                      className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm disabled:opacity-60"
                    >
                      Modifier
                    </button>

                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        duplicateTournament(tournament);
                      }}
                      className="px-3 py-2 rounded-lg bg-green-600 text-white text-sm disabled:opacity-60"
                    >
                      Dupliquer
                    </button>

                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleVisibility(tournament);
                      }}
                      className="px-3 py-2 rounded-lg bg-yellow-500 text-white text-sm disabled:opacity-60"
                    >
                      {tournament.visible ? 'Masquer' : 'Afficher'}
                    </button>

                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        archiveTournament(tournament);
                      }}
                      className="px-3 py-2 rounded-lg bg-gray-700 text-white text-sm disabled:opacity-60"
                    >
                      Archiver
                    </button>

                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        deleteTournament(tournament);
                      }}
                      className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm disabled:opacity-60"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl text-blue-800 text-sm">
        Le mode équipe utilise tournament_registrations et le moteur de tournoi existant. Le mode individuel utilise team_building_registrations et son tunnel dédié.
        La suppression retire seulement l'événement du catalogue public.
      </div>

      {selectedIndividualEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div><h2 className="text-2xl font-bold text-[#4c2f8e]">Participants – {selectedIndividualEvent.name}</h2><p className="text-gray-500">{individualRegistrations.length} inscription(s)</p></div>
              <button type="button" onClick={() => setSelectedIndividualEvent(null)} className="rounded-full bg-gray-100 px-4 py-2">Fermer</button>
            </div>
            {loadingIndividualRegistrations ? <p>Chargement...</p> : (
              <div className="space-y-3">
                {individualRegistrations.length === 0 && <p className="rounded-xl bg-gray-50 p-5 text-center text-gray-500">Aucune inscription.</p>}
                {individualRegistrations.map((registration) => (
                  <div key={registration.id} className="rounded-xl border p-4">
                    <p className="font-bold text-[#4c2f8e]">{registration.firstName} {registration.lastName}</p>
                    <p className="text-sm text-gray-600">{registration.email} · {registration.phone}</p>
                    {registration.company && <p className="text-sm text-gray-500">{registration.company}</p>}
                    <p className="mt-1 text-xs text-gray-500">{registration.isPaid ? 'Payé' : 'Gratuit'} · Langue : {registration.language || 'fr'}</p>
                    {registration.invoiceRequested && (
                      <div className="mt-2 rounded-lg bg-blue-50 p-2 text-xs text-blue-800">
                        Facture {registration.invoiceNumber || 'en attente'} · {registration.billingCompany} · {registration.vatNumber}<br />
                        {registration.billingStreet}, {registration.billingPostalCode} {registration.billingCity}, {registration.billingCountry}<br />
                        {registration.billingEmail}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTeamBuildingCatalog;
